import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Home, Plus, XCircle } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { apiErrorMessage, useToast } from '../lib/ToastContext';
import { EmptyState } from './ui/EmptyState';
import { Loader } from './ui/Spinner';

type WfhRequest = { id: string; fromDate: string; toDate: string; reason: string; status: string; reviewNote: string | null; createdAt: string };
const STATUS_STYLE: Record<string, string> = { pending: 'bg-amber-100 text-amber-700', approved: 'bg-brand-100 text-brand-700', rejected: 'bg-red-50 text-red-700', cancelled: 'bg-slate-100 text-slate-600' };
const range = (r: WfhRequest) => (r.fromDate === r.toDate ? r.fromDate : `${r.fromDate} → ${r.toDate}`);

/**
 * Self-service work-from-home: request a window, which a manager approves like leave. While
 * approved, check-in skips only the branch geofence — the position is still captured (see AttendanceCard).
 */
export function WfhRequestsCard() {
  const client = useQueryClient();
  const { user } = useAuth();
  const { success, error: toastError } = useToast();
  const [open, setOpen] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [reason, setReason] = useState('');
  const userId = user?.id ?? user?.sub;
  const requests = useQuery({ queryKey: ['wfh-requests', 'mine', userId], queryFn: async () => (await api.get<WfhRequest[]>('/api/wfh-requests', { params: { userId } })).data, enabled: !!userId });

  const invalidate = () => { void client.invalidateQueries({ queryKey: ['wfh-requests'] }); void client.invalidateQueries({ queryKey: ['me', 'branch'] }); };
  const submit = useMutation({
    mutationFn: () => api.post('/api/wfh-requests', { fromDate, toDate: toDate || fromDate, reason }),
    onSuccess: () => { invalidate(); success('Work-from-home request sent for approval'); setOpen(false); setFromDate(''); setToDate(''); setReason(''); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not submit request')),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api.post(`/api/wfh-requests/${id}/cancel`, {}),
    onSuccess: () => { invalidate(); success('Request cancelled'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not cancel')),
  });
  const rows = requests.data ?? [];

  return (
    <section className="card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-bold"><Home size={18} /> Work from home</h2>
          <p className="mt-1 text-sm text-muted">Not at your branch? Request approval to check in from home for a few days — GPS stays on and your location is still recorded.</p>
        </div>
        <button className="btn-secondary !px-3" aria-label="Request work from home" onClick={() => setOpen(!open)}><Plus size={16} /></button>
      </div>

      {open && (
        <form className="mt-4 grid gap-3 border-t border-border pt-4 sm:grid-cols-2" onSubmit={(e: FormEvent) => { e.preventDefault(); submit.mutate(); }}>
          <label className="label">From<input type="date" className="field mt-1" required value={fromDate} onChange={(e) => { setFromDate(e.target.value); if (toDate && toDate < e.target.value) setToDate(e.target.value); }} /></label>
          <label className="label">To<input type="date" className="field mt-1" min={fromDate} value={toDate} onChange={(e) => setToDate(e.target.value)} /></label>
          <label className="label sm:col-span-2">Reason<textarea className="field mt-1 min-h-20 py-2" required minLength={3} placeholder="e.g. Working from home this week" value={reason} onChange={(e) => setReason(e.target.value)} /></label>
          <div className="flex gap-2 sm:col-span-2">
            <button className="btn-primary" disabled={submit.isPending}>Send for approval</button>
            <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </form>
      )}

      <div className="mt-4 space-y-2">
        {requests.isLoading && <Loader className="!py-4" />}
        {!requests.isLoading && rows.length === 0 && !open && <EmptyState icon={Home} title="No work-from-home requests" hint="Requests you send appear here with their approval status." />}
        {rows.map((r) => (
          <div key={r.id} className="flex items-start justify-between gap-3 rounded-xl bg-canvas px-3 py-2">
            <div className="min-w-0">
              <p className="text-sm font-medium">{range(r)}</p>
              <p className="mt-0.5 text-xs text-muted">{r.reason}</p>
              {r.reviewNote && <p className="mt-0.5 text-xs italic text-muted">"{r.reviewNote}"</p>}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className={`badge ${STATUS_STYLE[r.status] ?? ''}`}>{r.status}</span>
              {(r.status === 'pending' || r.status === 'approved') && (
                <button className="text-muted hover:text-red-700" aria-label="Cancel request" disabled={cancel.isPending} onClick={() => { if (confirm('Cancel this request?')) cancel.mutate(r.id); }}><XCircle size={16} /></button>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
